// ============================================================
// Clerk authentication wiring (cloud-migration Phase 3)
// ============================================================
// Provides Clerk context + bridges the session token into the API client for
// EVERY route (public marketing + gated app). Unlike the pre-Phase-3 gate, it
// does NOT force a sign-in redirect at the root — public marketing routes must
// render for signed-out visitors. App routes opt into auth via <RequireAuth>.
// ============================================================

import React, { useEffect } from 'react';
import {
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useAuth,
} from '@clerk/clerk-react';
import { clearAuthTokenProvider, setAuthTokenProvider } from '../api/client';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
// Mirrors backend AUTH_DEV_BYPASS: skip Clerk entirely for local dev.
export const AUTH_DEV_BYPASS =
  (import.meta.env.VITE_AUTH_DEV_BYPASS as string | undefined) === 'true';

// Clerk is only actually mounted when we're not in dev-bypass and a key exists.
// This is a build-time constant, so binding hooks to it below is safe.
export const CLERK_ACTIVE = !AUTH_DEV_BYPASS && !!PUBLISHABLE_KEY;

function ClerkTokenBridge({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenProvider(async () => {
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
    return () => clearAuthTokenProvider();
  }, [getToken]);
  return <>{children}</>;
}

/**
 * Root provider. Supplies Clerk context + token bridge to the whole tree
 * without gating any route. Marketing routes render publicly; app routes are
 * gated by <RequireAuth>.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (AUTH_DEV_BYPASS) {
    return <>{children}</>;
  }
  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui' }}>
        <h1>Sign-in isn’t configured yet.</h1>
        <p>
          Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in <code>frontend/.env</code> and reload.
        </p>
      </div>
    );
  }
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <ClerkTokenBridge>{children}</ClerkTokenBridge>
    </ClerkProvider>
  );
}

/**
 * Wrap app-only routes. Signed-out visitors are redirected to Clerk sign-in and
 * never see protected content. In dev-bypass the children render directly.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!CLERK_ACTIVE) {
    return <>{children}</>;
  }
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

/** Safe signed-in check that also works in dev-bypass (always signed in). */
function useClerkSignedIn(): { isLoaded: boolean; isSignedIn: boolean } {
  const { isLoaded, isSignedIn } = useAuth();
  return { isLoaded, isSignedIn: !!isSignedIn };
}
function useAlwaysSignedIn(): { isLoaded: boolean; isSignedIn: boolean } {
  return { isLoaded: true, isSignedIn: true };
}
export const useIsSignedIn = CLERK_ACTIVE ? useClerkSignedIn : useAlwaysSignedIn;
